/**
 * Pure geometry and view-model helpers for the world map. Nothing here touches
 * MapLibre or the DOM except `renderPlaneIcon`, so the functions stay easy to
 * reason about and keep the simulation maths in the domain layer.
 */
import type { Feature, FeatureCollection, MultiLineString, Point, Polygon, Position } from 'geojson';
import { EARTH_RADIUS_KM, interpolateGreatCircle, sampleGreatCircle } from '../../../domain/geo/greatCircle';
import type { GeoPoint } from '../../../domain/geo/greatCircle';
import type {
  AirportView,
  FlightStatusSnapshot,
  LegView,
  MacroEvent,
} from '../../../domain/models/contracts';

/** Hubs need at least this many distinct network partners. */
const HUB_MIN_PARTNERS = 3;
const ARC_SEGMENTS = 64;
const CIRCLE_STEPS = 96;
/** A longitude jump larger than this between samples means the arc crossed the antimeridian. */
const ANTIMERIDIAN_JUMP_DEG = 180;
/** Lead distance, as a fraction of the great circle, used to measure heading. */
const HEADING_LOOKAHEAD = 0.01;
const MAX_ANIMATED_PROGRESS_PCT = 99.5;

export const PLANE_ICON_ID = 'flightline-plane';
const PLANE_ICON_PX = 32;
/** Nose points north (up) so that `icon-rotate` equals the true bearing. */
const PLANE_OUTLINE: readonly (readonly [number, number])[] = [
  [0, -10],
  [2, -3],
  [9, 1],
  [9, 3],
  [2, 1],
  [2, 6],
  [4.5, 8],
  [4.5, 9.5],
  [0, 8.5],
  [-4.5, 9.5],
  [-4.5, 8],
  [-2, 6],
  [-2, 1],
  [-9, 3],
  [-9, 1],
  [-2, -3],
];

export type AirportTier = 'HQ' | 'HUB' | 'NETWORK' | 'OTHER';

/**
 * Zoom at which an airport's marker appears. Player airports (HQ, hubs, every served airport) are always
 * drawn. Unserved airports are ranked by daily slot capacity: the largest show from the start, mid-size
 * airports from zoom 4, the rest from zoom 6.
 */
export type AirportMinZoom = 0 | 4 | 6;
export const AIRPORT_MIN_ZOOMS: readonly AirportMinZoom[] = [0, 4, 6];
const WORLD_MAJOR_COUNT = 60;
const WORLD_MID_COUNT = 400;

export interface AirportRole {
  readonly tier: AirportTier;
  readonly partnerCount: number;
}

type AirportProperties = { readonly id: string; readonly tier: AirportTier; readonly minZoom: AirportMinZoom };
type RouteProperties = { readonly key: string; readonly active: boolean; readonly width: number };
type PlaneProperties = { readonly registration: string; readonly legId: string; readonly bearing: number };
type ClosureProperties = { readonly id: string; readonly title: string; readonly intensity: number };

type Collection<G extends Point | MultiLineString | Polygon, P> = FeatureCollection<G, P>;

/**
 * Tier per airport. The HQ always wins; a hub is an airport with several
 * distinct network partners; any other served airport is a network spoke.
 */
export function classifyAirports(
  airports: readonly AirportView[],
  legs: readonly LegView[],
  hqAirportId: string,
): ReadonlyMap<string, AirportRole> {
  const partners = new Map<string, Set<string>>();
  const link = (from: string, to: string): void => {
    const set = partners.get(from) ?? new Set<string>();
    set.add(to);
    partners.set(from, set);
  };
  for (const leg of legs) {
    link(leg.origin, leg.destination);
    link(leg.destination, leg.origin);
  }

  const roles = new Map<string, AirportRole>();
  for (const airport of airports) {
    const partnerCount = partners.get(airport.id)?.size ?? 0;
    const tier: AirportTier =
      airport.id === hqAirportId
        ? 'HQ'
        : partnerCount >= HUB_MIN_PARTNERS
          ? 'HUB'
          : partnerCount > 0
            ? 'NETWORK'
            : 'OTHER';
    roles.set(airport.id, { tier, partnerCount });
  }
  return roles;
}

/** Initial bearing in degrees, clockwise from north, from `from` towards `to`. */
export function initialBearingDeg(from: GeoPoint, to: GeoPoint): number {
  const phi1 = toRad(from.lat);
  const phi2 = toRad(to.lat);
  const dLambda = toRad(to.lng - from.lng);
  const y = Math.sin(dLambda) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLambda);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Great-circle path as one or more runs, split wherever it crosses the antimeridian. */
export function greatCircleRuns(a: GeoPoint, b: GeoPoint): Position[][] {
  const runs: Position[][] = [];
  let current: Position[] = [];
  let previousLng: number | null = null;
  for (const point of sampleGreatCircle(a, b, ARC_SEGMENTS)) {
    if (previousLng !== null && Math.abs(point.lng - previousLng) > ANTIMERIDIAN_JUMP_DEG) {
      runs.push(current);
      current = [];
    }
    current.push([point.lng, point.lat]);
    previousLng = point.lng;
  }
  runs.push(current);
  return runs;
}

/**
 * One feature per airport pair, in either direction. Frequency widens the line
 * and an active leg on the pair highlights it.
 */
export function buildRouteCollection(
  legs: readonly LegView[],
  airportsById: ReadonlyMap<string, AirportView>,
  activeLegIds: ReadonlySet<string>,
): Collection<MultiLineString, RouteProperties> {
  const pairs = new Map<string, { a: AirportView; b: AirportView; frequency: number; active: boolean }>();
  for (const leg of legs) {
    const origin = airportsById.get(leg.origin);
    const destination = airportsById.get(leg.destination);
    if (origin === undefined || destination === undefined) continue;
    const [a, b] = leg.origin < leg.destination ? [origin, destination] : [destination, origin];
    const key = `${a.id}-${b.id}`;
    const active = activeLegIds.has(leg.id);
    const existing = pairs.get(key);
    if (existing === undefined) {
      pairs.set(key, { a, b, frequency: 1, active });
    } else {
      existing.frequency += 1;
      existing.active = existing.active || active;
    }
  }

  const features: Feature<MultiLineString, RouteProperties>[] = [];
  for (const [key, pair] of pairs) {
    features.push({
      type: 'Feature',
      properties: { key, active: pair.active, width: Math.min(3.2, 1 + pair.frequency * 0.4) },
      geometry: { type: 'MultiLineString', coordinates: greatCircleRuns(pair.a, pair.b) },
    });
  }
  return { type: 'FeatureCollection', features };
}

export function buildAirportCollection(
  airports: readonly AirportView[],
  roles: ReadonlyMap<string, AirportRole>,
): Collection<Point, AirportProperties> {
  const dailyCapacity = (a: AirportView): number => a.slotCapacityHourly.reduce((sum, v) => sum + v, 0);
  const bySize = [...airports].sort((a, b) => dailyCapacity(b) - dailyCapacity(a) || a.id.localeCompare(b.id));
  const worldRank = new Map(bySize.map((a, index) => [a.id, index] as const));
  const minZoomOf = (id: string, tier: AirportTier): AirportMinZoom => {
    if (tier !== 'OTHER') return 0;
    const rank = worldRank.get(id) ?? Number.MAX_SAFE_INTEGER;
    return rank < WORLD_MAJOR_COUNT ? 0 : rank < WORLD_MID_COUNT ? 4 : 6;
  };
  const features: Feature<Point, AirportProperties>[] = airports.map((airport) => ({
    type: 'Feature',
    properties: {
      id: airport.id,
      tier: roles.get(airport.id)?.tier ?? 'OTHER',
      minZoom: minZoomOf(airport.id, roles.get(airport.id)?.tier ?? 'OTHER'),
    },
    geometry: { type: 'Point', coordinates: [airport.lng, airport.lat] },
  }));
  return { type: 'FeatureCollection', features };
}

/**
 * Progress in percent, eased from where the aircraft was at the previous hourly
 * tick towards where it is at the latest one. The worker only reports flights at
 * each tick, so a flight dispatched mid-hour is first seen part way along; easing
 * from its previous position (the airport, when it was not yet airborne) keeps it
 * from appearing mid-route. Clamped below arrival so a flight never jumps onto
 * the destination before the worker reports it.
 */
export function animatedProgressPct(flight: FlightStatusSnapshot, previousPct: number, hourFraction: number): number {
  const eased = previousPct + (flight.progressPct - previousPct) * hourFraction;
  return Math.min(MAX_ANIMATED_PROGRESS_PCT, eased);
}

/** Progress per flight at a tick, keyed by `flightKey`, for easing from one tick to the next. */
export function progressByFlight(flights: readonly FlightStatusSnapshot[]): Map<string, number> {
  return new Map(flights.map((flight) => [flightKey(flight), flight.progressPct] as const));
}

export function flightKey(flight: FlightStatusSnapshot): string {
  return `${flight.legId}|${flight.aircraftReg}`;
}

/**
 * Aircraft positions along their great circles, with heading for the icon rotation.
 * `previousProgress` is the progress at the tick before the latest one, or null when
 * there is no earlier tick to ease from (for example just after a save is loaded).
 */
export function buildPlaneCollection(
  flights: readonly FlightStatusSnapshot[],
  airportsById: ReadonlyMap<string, AirportView>,
  previousProgress: ReadonlyMap<string, number> | null,
  hourFraction: number,
): Collection<Point, PlaneProperties> {
  const features: Feature<Point, PlaneProperties>[] = [];
  for (const flight of flights) {
    const origin = airportsById.get(flight.origin);
    const destination = airportsById.get(flight.destination);
    if (origin === undefined || destination === undefined) continue;

    const previousPct = previousProgress === null ? flight.progressPct : (previousProgress.get(flightKey(flight)) ?? 0);
    const fraction = animatedProgressPct(flight, previousPct, hourFraction) / 100;
    const here = interpolateGreatCircle(origin, destination, fraction);
    const ahead = interpolateGreatCircle(origin, destination, Math.min(1, fraction + HEADING_LOOKAHEAD));
    features.push({
      type: 'Feature',
      properties: { registration: flight.aircraftReg, legId: flight.legId, bearing: initialBearingDeg(here, ahead) },
      geometry: { type: 'Point', coordinates: [here.lng, here.lat] },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** A flight the worker dropped at the latest tick, with the leg block time it was flying to. */
export interface ArrivingFlight {
  readonly flight: FlightStatusSnapshot;
  readonly blockMinutes: number;
}

/**
 * Flights that were in the previous tick but not the latest one landed in between. The worker only drops them
 * on the tick, so they are kept on screen until their touchdown.
 */
export function arrivingFlights(
  previousFlights: readonly FlightStatusSnapshot[],
  latestProgress: ReadonlyMap<string, number>,
  legsById: ReadonlyMap<string, LegView>,
): ArrivingFlight[] {
  const arrivals: ArrivingFlight[] = [];
  for (const flight of previousFlights) {
    if (latestProgress.has(flightKey(flight))) continue;
    const leg = legsById.get(flight.legId);
    if (leg === undefined || leg.blockMinutes <= 0) continue;
    arrivals.push({ flight, blockMinutes: leg.blockMinutes });
  }
  return arrivals;
}

/**
 * Landed aircraft on the last of their approach. The progress left at the previous tick, at the leg's block
 * speed, gives the time to touchdown, which is always under an hour. Progress runs from that point to 100% at
 * touchdown and the aircraft is dropped there. `hourFraction` uses the same clock as `buildPlaneCollection`.
 */
export function buildArrivalFeatures(
  arrivals: readonly ArrivingFlight[],
  airportsById: ReadonlyMap<string, AirportView>,
  hourFraction: number,
): Feature<Point, PlaneProperties>[] {
  const features: Feature<Point, PlaneProperties>[] = [];
  for (const { flight, blockMinutes } of arrivals) {
    const origin = airportsById.get(flight.origin);
    const destination = airportsById.get(flight.destination);
    if (origin === undefined || destination === undefined) continue;

    const startPct = flight.progressPct;
    const remainingHours = ((100 - startPct) / 100) * (blockMinutes / 60);
    if (remainingHours <= 0 || hourFraction >= remainingHours) continue;

    const fraction = (startPct + ((100 - startPct) * hourFraction) / remainingHours) / 100;
    const here = interpolateGreatCircle(origin, destination, fraction);
    const ahead = interpolateGreatCircle(origin, destination, Math.min(1, fraction + HEADING_LOOKAHEAD));
    features.push({
      type: 'Feature',
      properties: { registration: flight.aircraftReg, legId: flight.legId, bearing: initialBearingDeg(here, ahead) },
      geometry: { type: 'Point', coordinates: [here.lng, here.lat] },
    });
  }
  return features;
}

/** Ring of `CIRCLE_STEPS` points at `radiusKm` from `centre`. Longitudes are left unwrapped so the ring stays continuous across the antimeridian. */
export function circleRing(centre: GeoPoint, radiusKm: number): Position[] {
  const delta = radiusKm / EARTH_RADIUS_KM;
  const phi1 = toRad(centre.lat);
  const lambda1 = toRad(centre.lng);
  const ring: Position[] = [];
  for (let i = 0; i <= CIRCLE_STEPS; i += 1) {
    const theta = (2 * Math.PI * i) / CIRCLE_STEPS;
    const phi2 = Math.asin(Math.sin(phi1) * Math.cos(delta) + Math.cos(phi1) * Math.sin(delta) * Math.cos(theta));
    const lambda2 =
      lambda1 +
      Math.atan2(Math.sin(theta) * Math.sin(delta) * Math.cos(phi1), Math.cos(delta) - Math.sin(phi1) * Math.sin(phi2));
    ring.push([toDeg(lambda2), toDeg(phi2)]);
  }
  return ring;
}

/**
 * Circular danger zones for airspace closures that are in force. Announced
 * closures are not drawn yet; resolving ones fade out with their intensity.
 */
export function buildClosureCollection(events: readonly MacroEvent[]): Collection<Polygon, ClosureProperties> {
  const features: Feature<Polygon, ClosureProperties>[] = [];
  for (const event of events) {
    if (event.category !== 'AIRSPACE_CLOSURE') continue;
    if (event.state !== 'ACTIVE' && event.state !== 'RESOLVING') continue;
    const scope = event.scope;
    if (scope.kind !== 'REGIONAL' || scope.zone === null) continue;
    features.push({
      type: 'Feature',
      properties: { id: event.id, title: event.title, intensity: event.intensity },
      geometry: { type: 'Polygon', coordinates: [circleRing(scope.zone, scope.zone.radiusKm)] },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** Rasterised aircraft glyph for `map.addImage`. Returns null when no 2D canvas is available. */
export function renderPlaneIcon(fill: string, outline: string): ImageData | null {
  const canvas = document.createElement('canvas');
  canvas.width = PLANE_ICON_PX;
  canvas.height = PLANE_ICON_PX;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;

  const scale = PLANE_ICON_PX / 24;
  ctx.translate(PLANE_ICON_PX / 2, PLANE_ICON_PX / 2);
  ctx.scale(scale, scale);
  ctx.beginPath();
  PLANE_OUTLINE.forEach(([x, y], index) => {
    if (index === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1 / scale;
  ctx.fill();
  ctx.stroke();
  return ctx.getImageData(0, 0, PLANE_ICON_PX, PLANE_ICON_PX);
}

export const EMPTY_COLLECTION: FeatureCollection = { type: 'FeatureCollection', features: [] };

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function toDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}
